import { GridAlpha, Flex, Heading } from '@applyboard/crystal-ui';
import { StudentOutlineIcon } from '@applyboard/ui-icons';
import { formatNonTimezoneDate } from '~/utils/formatDate';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';

import { Card } from './Card';
import { AttachedDocuments } from './shared/AttachedDocuments';
import { StudentSchoolInformation } from '@ab/ams-school-api/src/services/studentsService/v1/studentsSerivce/types';
import { EditSingleField } from '~/components/uie-fix/EditSingleField';
import { usePatchStudent } from '../../data/usePatchStudent';

export function EducationHistory() {
  const {
    application: { educationHistories, studentPersonaInformation },
  } = useAppProcessing();

  return (
    <Card id="educationHistory" icon={StudentOutlineIcon} title="Education History">
      <Flex justify="start" direction="column" gap={10}>
        {educationHistories.educationHistoryItems.map((eh) => (
          <Flex key={eh.key} justify="start" direction="column" gap={8}>
            <GridAlpha rows="repeat(1, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
              <GridAlpha.Item rowStart={1} columnStart={1} columnEnd={3}>
                <LabelValue label="CREDENTIAL GAINED" variant="titleS" value={eh.credentialReceived} />
              </GridAlpha.Item>

              <GridAlpha.Item rowStart={2} columnStart={1} columnEnd={1}>
                <LabelValue label="EDUCATION LEVEL" value={eh.level.name} />
              </GridAlpha.Item>
              <GridAlpha.Item rowStart={2} columnStart={2} columnEnd={2}>
                <LabelValue label="EDUCATION COUNTRY" value={eh.country.countryLabel} />
              </GridAlpha.Item>

              <GridAlpha.Item rowStart={3} columnStart={1} columnEnd={1}>
                <LabelValue label="START DATE" value={formatNonTimezoneDate(new Date(eh.startDate))} />
              </GridAlpha.Item>
              <GridAlpha.Item rowStart={3} columnStart={2} columnEnd={2}>
                <LabelValue label="END DATE" value={formatNonTimezoneDate(new Date(eh.endDate))} />
              </GridAlpha.Item>
            </GridAlpha>
            <AttachedDocuments
              showTitle
              attachmentDocuments={eh.documents}
              section="educationHistory"
              sectionReference={eh.key}
            />
          </Flex>
        ))}
        <RelevantStudentInformation
          schoolInformation={educationHistories.schoolInformation}
          studentId={studentPersonaInformation?.id}
        />
      </Flex>
    </Card>
  );
}

type EducationNumbersData = {
  label: string;
  value: string;
};

type RelevantStudentInformationProps = Readonly<{
  schoolInformation?: StudentSchoolInformation;
  studentId?: string;
}>;

function RelevantStudentInformation({ schoolInformation, studentId }: RelevantStudentInformationProps) {
  const { isPatchingStudent, patchStudent } = usePatchStudent();
  const educationNumbersData: EducationNumbersData[] = [];

  if (schoolInformation) {
    const studentSchoolInformationKeys = Object.keys(schoolInformation) as Array<keyof StudentSchoolInformation>;
    const educationNumberType = studentSchoolInformationKeys.filter((key) => key !== 'haveAttendedSchool'); // we should have only one item in real scenario

    educationNumberType.forEach((type) => {
      let educationNumberTypeLabel: string | undefined;

      switch (type) {
        case 'ontarioEducationNumber':
          educationNumberTypeLabel = 'OEN';
          break;
        case 'albertaStudentNumber':
          educationNumberTypeLabel = 'ASN';
          break;
        case 'personalEducationNumber':
          educationNumberTypeLabel = 'PEN';
          break;
      }

      const educationNumberValue = schoolInformation[type]!;

      if (educationNumberTypeLabel) {
        educationNumbersData.push({
          label: educationNumberTypeLabel,
          value: educationNumberValue,
        });
      }
    });
  }

  return (
    <Flex justify="start" direction="column" gap={10}>
      <Heading level={4}>Relevant student information</Heading>
      <GridAlpha rows="repeat(1, auto)" rowGap={6}>
        {educationNumbersData.map((data, index) => (
          <GridAlpha.Item key={`relevant-student-information-${index}`}>
            <LabelValue label={data.label} value={data.value} />
          </GridAlpha.Item>
        ))}
        {studentId ? (
          <GridAlpha.Item>
            <EditSingleField
              label="STUDENT ID"
              value={schoolInformation?.schoolAssignedStudentId ?? ''}
              isLoading={isPatchingStudent}
              validate={(value) => {
                const regex = new RegExp(/^(?! )([\w -]*)(?<! )$/);
                if (value !== '') {
                  if (value.length > 15) {
                    return 'Student ID must no more than 15 characters long.';
                  }
                  if (!regex.test(value)) {
                    return 'Student ID can only include letters, numbers, hyphens, and underscores.';
                  }
                }
                return true;
              }}
              onSave={(value) => {
                return new Promise((resolve, reject) => {
                  patchStudent(
                    {
                      id: studentId,
                      payload: {
                        schoolInformation: {
                          schoolAssignedStudentId: value !== '' ? value : null,
                        },
                      },
                    },
                    {
                      onSuccess: () => {
                        resolve();
                      },
                      onError: (error) => {
                        reject(error as Error);
                      },
                    }
                  );
                });
              }}
            />
          </GridAlpha.Item>
        ) : null}
      </GridAlpha>
    </Flex>
  );
}
